/**
 * Form validation utilities
 */

/**
 * Validation rules for different field types
 */
export const validationRules = {
  required: (value) => {
    if (value === null || value === undefined || value === '') {
      return 'This field is required';
    }
    return null;
  },

  email: (value) => {
    if (!value) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value) ? null : 'Please enter a valid email address';
  },

  phone: (value) => {
    if (!value) return null;
    const phoneRegex = /^[\d\s\-()]+$/;
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length < 10) {
      return 'Phone number must be at least 10 digits';
    }
    return phoneRegex.test(value) ? null : 'Please enter a valid phone number';
  },

  number: (value) => {
    if (!value && value !== 0) return null;
    return !isNaN(value) ? null : 'Please enter a valid number';
  },

  positiveNumber: (value) => {
    if (!value && value !== 0) return null;
    const num = parseFloat(value);
    if (isNaN(num)) return 'Please enter a valid number';
    return num >= 0 ? null : 'Please enter a positive number';
  },

  integer: (value) => {
    if (!value && value !== 0) return null;
    return Number.isInteger(Number(value))
      ? null
      : 'Please enter a whole number';
  },

  employeeNumber: (value) => {
    if (!value) return null;
    const num = String(value).replace(/\D/g, '');
    if (num.length < 4) {
      return 'Employee number must be at least 4 digits';
    }
    return null;
  },

  name: (value) => {
    if (!value) return null;
    const nameRegex = /^[A-Za-z\s\-'.]+$/;
    return nameRegex.test(value)
      ? null
      : 'Please enter a valid name (letters, spaces, hyphens, apostrophes only)';
  },

  address: (value) => {
    if (!value) return null;
    const addressRegex = /^[A-Za-z0-9\s\-#.,']+$/;
    return addressRegex.test(value) ? null : 'Please enter a valid address';
  },

  city: (value) => {
    if (!value) return null;
    const cityRegex = /^[A-Za-z\s\-.]+$/;
    return cityRegex.test(value) ? null : 'Please enter a valid city name';
  },

  zip: (value) => {
    if (!value) return null;
    const zipRegex = /^\d{5}(-\d{4})?$/;
    return zipRegex.test(value)
      ? null
      : 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
  },

  state: (value) => {
    if (!value) return null;
    return value.length === 2 ? null : 'Please select a state';
  },

  date: (value) => {
    if (!value) return null;
    const date = new Date(value);
    return !isNaN(date.getTime()) ? null : 'Please enter a valid date';
  },

  futureDate: (value) => {
    if (!value) return null;
    const date = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (isNaN(date.getTime())) {
      return 'Please enter a valid date';
    }
    return date >= today ? null : 'Date cannot be in the past';
  },

  pastDate: (value) => {
    if (!value) return null;
    const date = new Date(value);
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (isNaN(date.getTime())) {
      return 'Please enter a valid date';
    }
    return date <= today ? null : 'Date cannot be in the future';
  },

  minLength: (min) => (value) => {
    if (!value) return null;
    return value.length >= min ? null : `Must be at least ${min} characters`;
  },

  maxLength: (max) => (value) => {
    if (!value) return null;
    return value.length <= max
      ? null
      : `Must be no more than ${max} characters`;
  },

  minValue: (min) => (value) => {
    if (!value && value !== 0) return null;
    const num = parseFloat(value);
    if (isNaN(num)) return 'Please enter a valid number';
    return num >= min ? null : `Must be at least ${min}`;
  },

  maxValue: (max) => (value) => {
    if (!value && value !== 0) return null;
    const num = parseFloat(value);
    if (isNaN(num)) return 'Please enter a valid number';
    return num <= max ? null : `Must be no more than ${max}`;
  },

  signature: (value) => {
    if (!value) return 'Signature is required';
    return null;
  },

  file: (value) => {
    if (!value) return 'File is required';
    return null;
  },

  fileSize: (maxSizeMB) => (value) => {
    if (!value) return null;
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (value.size > maxBytes) {
      return `File size must be less than ${maxSizeMB}MB`;
    }
    return null;
  },

  fileType: (allowedTypes) => (value) => {
    if (!value) return null;
    const fileType = value.type || '';
    const fileName = value.name || '';
    const extension = fileName.split('.').pop()?.toLowerCase();

    const isAllowed = allowedTypes.some((type) => {
      if (type.includes('/')) {
        return fileType === type;
      } else {
        return extension === type.replace('.', '');
      }
    });

    return isAllowed
      ? null
      : `File type must be one of: ${allowedTypes.join(', ')}`;
  },
};

/**
 * Field configurations for each form
 */
export const formValidations = {
  backPay: {
    'employee.employeeNumber': [
      validationRules.required,
      validationRules.employeeNumber,
    ],
    'employee.employeeFirst': [validationRules.required, validationRules.name],
    'employee.employeeLast': [validationRules.required, validationRules.name],
    'store.id': [validationRules.required],
  },

  bonus: {
    'employee.employeeNumber': [
      validationRules.required,
      validationRules.employeeNumber,
    ],
    'employee.employeeFirst': [validationRules.required, validationRules.name],
    'employee.employeeLast': [validationRules.required, validationRules.name],
  },

  hotel: {
    'store.id': [validationRules.required],
    checkIn: [validationRules.required, validationRules.date],
    checkOut: [validationRules.required, validationRules.date],
    numOfRooms: [
      validationRules.required,
      validationRules.integer,
      validationRules.minValue(1),
    ],
    numOfPeople: [
      validationRules.required,
      validationRules.integer,
      validationRules.minValue(1),
    ],
    hotelReason: [validationRules.required],
    workTicket: [validationRules.required],
  },

  mileage: {
    'employee.employeeNumber': [
      validationRules.required,
      validationRules.employeeNumber,
    ],
    'employee.employeeFirst': [validationRules.required, validationRules.name],
    'employee.employeeLast': [validationRules.required, validationRules.name],
  },

  nonMnMileage: {
    'employee.employeeNumber': [
      validationRules.required,
      validationRules.employeeNumber,
    ],
    'employee.employeeFirst': [validationRules.required, validationRules.name],
    'employee.employeeLast': [validationRules.required, validationRules.name],
  },

  newHire: {
    hireType: [validationRules.required],
    'employee.employeeFirst': [validationRules.required, validationRules.name],
    'employee.employeeLast': [validationRules.required, validationRules.name],
    // Email is optional / not validated for New Hire
    // 'employee.email': [validationRules.required, validationRules.email],
    'employee.phone1': [validationRules.required, validationRules.phone],
    'employee.ssn': [validationRules.required],
    'employee.dob': [validationRules.required, validationRules.date],
    'employee.gender': [validationRules.required],
    'employee.address.address1': [
      validationRules.required,
      validationRules.address,
    ],
    'employee.address.city': [validationRules.required, validationRules.city],
    'employee.address.state': [validationRules.required],
    'employee.address.zip': [validationRules.required, validationRules.zip],
    'store.id': [validationRules.required],
    position: [validationRules.required],
    payRate: [validationRules.required, validationRules.positiveNumber],
    firstDayWorked: [validationRules.required, validationRules.date],
    preferredLanguage: [validationRules.required],
    documentTitle: [validationRules.required],
    documentNumber: [validationRules.required],
    documentExp: [validationRules.required, validationRules.date],
  },

  perDiem: {
    'employee.employeeNumber': [
      validationRules.required,
      validationRules.employeeNumber,
    ],
    'employee.employeeFirst': [validationRules.required, validationRules.name],
    'employee.employeeLast': [validationRules.required, validationRules.name],
    'store.id': [validationRules.required],
    firstHotelNight: [validationRules.required, validationRules.date],
    lastHotelNight: [validationRules.required, validationRules.date],
    arrivalDate: [validationRules.required, validationRules.date],
    departureDate: [validationRules.required, validationRules.date],
  },

  pto: {
    'employee.employeeNumber': [
      validationRules.required,
      validationRules.employeeNumber,
    ],
    'employee.employeeFirst': [validationRules.required, validationRules.name],
    'employee.employeeLast': [validationRules.required, validationRules.name],
    department: [validationRules.required],
    absenceFrom: [validationRules.required, validationRules.date],
    absenceTo: [validationRules.required, validationRules.date],
    hours: [
      validationRules.required,
      validationRules.positiveNumber,
      validationRules.maxValue(80),
    ],
  },

  targetOrder: {
    'store.id': [validationRules.required],
  },

  termination: {
    'employee.employeeNumber': [
      validationRules.required,
      validationRules.employeeNumber,
    ],
    'employee.employeeFirst': [validationRules.required, validationRules.name],
    'employee.employeeLast': [validationRules.required, validationRules.name],
    lastDayWorked: [validationRules.required, validationRules.date],
    termType: [validationRules.required],
  },

  timeAdjustment: {
    'employee.employeeNumber': [
      validationRules.required,
      validationRules.employeeNumber,
    ],
    'employee.employeeFirst': [validationRules.required, validationRules.name],
    'employee.employeeLast': [validationRules.required, validationRules.name],
  },

  uniform: {
    'employee.employeeNumber': [
      validationRules.required,
      validationRules.employeeNumber,
    ],
    'employee.employeeFirst': [validationRules.required, validationRules.name],
    'employee.employeeLast': [validationRules.required, validationRules.name],
    'employee.address.address1': [
      validationRules.required,
      validationRules.address,
    ],
    'employee.address.city': [validationRules.required, validationRules.city],
    'employee.address.state': [validationRules.required],
    'employee.address.zip': [validationRules.required, validationRules.zip],
    quantity: [
      validationRules.required,
      validationRules.integer,
      validationRules.minValue(1),
    ],
    size: [validationRules.required],
  },

  workTicket: {
    'employee.employeeNumber': [
      validationRules.required,
      validationRules.employeeNumber,
    ],
    'employee.employeeFirst': [validationRules.required, validationRules.name],
    'employee.employeeLast': [validationRules.required, validationRules.name],
    'projectLocation.id': [validationRules.required],
    typeOfWork: [validationRules.required],
    isBillable: [validationRules.required],
    supplyOrderSubmitted: [validationRules.required],
    supplyOrderDate: [validationRules.date],
    projectStartDate: [validationRules.required, validationRules.date],
    projectEndDate: [validationRules.required, validationRules.date],
  },
};

/**
 * Array field validations for forms with dynamic arrays
 * Structure: { formName: { arrayPath: { fieldName: [validations] } } }
 */
export const arrayFieldValidations = {
  backPay: {
    backPayDates: {
      date: [validationRules.required, validationRules.date],
      punchIn: [validationRules.required],
      punchOut: [validationRules.required],
    },
  },

  bonus: {
    bonuses: {
      date: [validationRules.required, validationRules.date],
      'store.id': [validationRules.required],
      bonus: [validationRules.required, validationRules.positiveNumber],
    },
  },

  mileage: {
    mileageDates: {
      date: [validationRules.required, validationRules.date],
      'location1.id': [validationRules.required],
      'location2.id': [validationRules.required],
    },
  },

  nonMnMileage: {
    mileageDates: {
      date: [validationRules.required, validationRules.date],
      'location1.id': [validationRules.required],
      'location2.id': [validationRules.required],
    },
  },

  perDiem: {
    mileageDates: {
      date: [validationRules.required, validationRules.date],
      'location1.id': [validationRules.required],
      'location2.id': [validationRules.required],
    },
  },

  timeAdjustment: {
    breaks: {
      date: [validationRules.required, validationRules.date],
      punchIn: [validationRules.required],
      punchOut: [validationRules.required],
    },
  },

  targetOrder: {
    targetOrderItems: {
      name: [validationRules.required],
      quantity: [
        validationRules.required,
        validationRules.integer,
        validationRules.minValue(1),
      ],
    },
  },
};

/**
 * Get nested value from object using dot notation
 * Supports array indexing: 'bonuses[0].date' or 'bonuses.0.date'
 */
const getNestedValue = (obj, path) => {
  // Handle array notation: bonuses[0].date -> bonuses.0.date
  const normalizedPath = path.replace(/\[(\d+)\]/g, '.$1');
  return normalizedPath
    .split('.')
    .reduce((current, key) => current?.[key], obj);
};

/**
 * Validate a single field
 */
export const validateField = (formName, fieldName, value, formData = null) => {
  const validations = formValidations[formName];
  if (!validations || !validations[fieldName]) {
    return null;
  }

  const fieldValidations = validations[fieldName];

  for (const validate of fieldValidations) {
    const error = validate(value, formData);
    if (error) {
      return error;
    }
  }

  return null;
};

/**
 * Validate array item field
 * @param {string} formName - The form name
 * @param {string} arrayPath - The path to the array (e.g., 'bonuses')
 * @param {number} index - The array index
 * @param {string} fieldName - The field name within the array item (e.g., 'date')
 * @param {*} value - The field value
 * @param {object} formData - The complete form data
 */
export const validateArrayField = (
  formName,
  arrayPath,
  index,
  fieldName,
  value,
  formData = null
) => {
  const fullPath = `${arrayPath}.${fieldName}`;
  return validateField(formName, fullPath, value, formData);
};

/**
 * Validate array items in form data
 * @param {string} formName - The form name
 * @param {string} arrayPath - The path to the array (e.g., 'bonuses', 'mileageDates')
 * @param {Array} arrayData - The array to validate
 * @param {object} validations - The validation rules for array fields
 */
export const validateArray = (formName, arrayPath, arrayData, validations) => {
  const errors = {};

  if (!Array.isArray(arrayData) || !validations) {
    return errors;
  }

  arrayData.forEach((item, index) => {
    for (const fieldName in validations) {
      // Use getNestedValue to handle nested properties like 'store.id' or 'location1.id'
      const value = getNestedValue(item, fieldName);

      // Apply validation rules directly (don't use validateField which looks in formValidations)
      const fieldValidations = validations[fieldName];
      for (const validate of fieldValidations) {
        const error = validate(value, item);
        if (error) {
          // Store error with array index: bonuses[0].date
          const errorKey = `${arrayPath}[${index}].${fieldName}`;
          errors[errorKey] = error;
          break; // Stop at first error for this field
        }
      }
    }
  });

  return errors;
};

/**
 * Validate entire form including arrays
 */
export const validateForm = (formName, formData) => {
  const validations = formValidations[formName];
  if (!validations) {
    return { isValid: true, errors: {} };
  }

  const errors = {};

  // Validate regular fields
  for (const fieldName in validations) {
    const value = getNestedValue(formData, fieldName);
    const error = validateField(formName, fieldName, value, formData);

    if (error) {
      errors[fieldName] = error;
    }
  }

  // Validate array fields based on form-specific configurations
  const arrayValidations = arrayFieldValidations[formName];
  if (arrayValidations) {
    for (const arrayPath in arrayValidations) {
      const arrayData = getNestedValue(formData, arrayPath);
      const arrayErrors = validateArray(
        formName,
        arrayPath,
        arrayData,
        arrayValidations[arrayPath]
      );
      Object.assign(errors, arrayErrors);
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Get error message for a field
 */
export const getFieldError = (errors, fieldName) => {
  return errors?.[fieldName] || null;
};

/**
 * Check if a field has an error
 */
export const hasFieldError = (errors, fieldName) => {
  return !!errors?.[fieldName];
};

/**
 * Format validation errors for display
 */
export const formatValidationErrors = (errors) => {
  return Object.entries(errors).map(([field, message]) => ({
    field,
    message,
    displayField: field.split('.').pop(), // Get the last part of nested field name
  }));
};
